import React, { useState } from 'react';

interface RuleDef {
  originalIndex: number;
  range?: string;
  cellId?: string; // Fallback for older rules
  condition: string;
  format: { bg?: string; color?: string; [key: string]: any };
}

interface ConditionalFormatModalProps {
  isOpen: boolean;
  activeRules: RuleDef[];
  onClose: () => void;
  onSave: (condition: string, bg: string) => void;
  onDelete: (index: number) => void;
}

export const ConditionalFormatModal: React.FC<ConditionalFormatModalProps> = ({ isOpen, activeRules, onClose, onSave, onDelete }) => {
  const [condition, setCondition] = useState('');
  const [bg, setBg] = useState('#fef08a');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (condition && bg) {
      onSave(condition, bg);
      setCondition('');
      setBg('#fef08a');
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
      <div className="bg-white p-6 rounded-lg shadow-2xl border border-gray-300 w-full max-w-md max-h-[90vh] flex flex-col pointer-events-auto">
        <div className="flex justify-between items-center mb-4 shrink-0">
          <h2 className="text-xl font-bold text-gray-800">Conditional Formatting</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-[150px] mb-4 border border-gray-200 rounded p-2 bg-gray-50">
            {activeRules.length === 0 ? (
                <div className="text-sm text-gray-500 text-center py-8">No active rules on this sheet.</div>
            ) : (
                <div className="space-y-2">
                    {activeRules.map((rule) => (
                        <div key={rule.originalIndex} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200 shadow-sm text-sm">
                            <div className="flex items-center space-x-3">
                                <div className="w-6 h-6 rounded border border-gray-300" style={{ backgroundColor: rule.format.bg || '#fff' }}></div>
                                <div>
                                    <span className="font-bold text-gray-700">{rule.range || rule.cellId || 'ALL'}</span>
                                    <span className="mx-2 text-gray-400">|</span>
                                    <span className="font-mono text-blue-600">{rule.condition}</span>
                                </div>
                            </div>
                            <button onClick={() => onDelete(rule.originalIndex)} className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4 shrink-0 border-t border-gray-200 pt-4">
          <h3 className="font-bold text-sm text-gray-700">Add New Rule</h3>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Condition</label>
            <input
              type="text"
              placeholder="e.g., >100, =TRUE, <0, Apple"
              required
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 outline-none text-sm"
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">Applies to the currently selected range.</p>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Highlight Color</label>
            <div className="flex items-center space-x-2">
                <input
                  type="color"
                  className="w-10 h-10 p-0 border-0 rounded cursor-pointer shrink-0"
                  value={bg}
                  onChange={(e) => setBg(e.target.value)}
                />
                <input
                  type="text"
                  required
                  className="flex-1 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 outline-none uppercase text-sm"
                  value={bg}
                  onChange={(e) => setBg(e.target.value)}
                />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium transition text-sm">
              Done
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 font-medium shadow-sm transition text-sm">
              Add Rule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};